import router from "@/router";
import {v4 as uuidv4} from 'uuid';
import {defineStore} from "pinia";
import {appAxios} from '@/app.axios';
import UserService from "@/services/user.service";
import {useTenantStore} from "@/stores/tenantStore";
import {Application, Role, User} from '@/types';
import GroupService from "@/services/group.service";
import ApplicationService from "@/services/application.service";

interface State {
  appLoading?: boolean;
  user?: any;
  appFrameworkToken: string | null;
  preLoginRoute: string | null;
  permissions: string[];
  toastErrors: any;
  debugMode: boolean;
  apps: Application[];
}

export const useUserStore = defineStore( 'appPortalUserStore', {
  state: (): State => ({
    user: {},
    appFrameworkToken: null,
    preLoginRoute: '',
    permissions: [],
    toastErrors: [],
    debugMode: false,
    apps: []
  }),
  getters: {
    isLoggedIn: state => {
      return !!state.appFrameworkToken;
    },
    hasPermission: (state) => (permission: string) => {
      return state.permissions.includes(permission);
    },
    isManager: state => {
      return state.user?.roles?.map((r: Role) => r.id).includes(1);
    },
    isSuperAdmin: state => {
      return state.user?.roles?.map((r: Role) => r.id).includes(3);
    },
    lastActiveApplication: () => Number(localStorage.getItem('application_framework_last_application')) || null,
  },
  actions: {
    async setLastActiveApplication(appId: number | null) {
      localStorage.setItem('application_framework_last_application', appId ? appId.toString() : '')
    },
    async userLogout() {
      const tenantStore = useTenantStore();
      await tenantStore.getTenantTheme();
      // Clear token
      this.appFrameworkToken = null;
      localStorage.removeItem('token')

      // Clear user
      this.user = null;

      // Clear tenant ID
      localStorage.removeItem('application_framework_tenant')
      tenantStore.tenantId = null;

      await router.replace({ name: 'login' });
    },
    setToken(token: string) {
      this.appFrameworkToken = token;
      appAxios.defaults.headers.common.Authorization = `Bearer ${token}`;
      setLocalStorage('token', token);
    },
    async userLogin(jwt: string) {
      // Set token
      this.setToken(jwt)

      let route: { name?: string; params?: any; path?: string } = { path: '/' }
      if (this.preLoginRoute) {
        const preLoginRoute: any = this.preLoginRoute
        if (preLoginRoute.name) {
          route = preLoginRoute;
        }
        this.preLoginRoute = null;
      }
      await this.refreshUser();

      if (this.user?.roles.map((r: Role) => r.id).includes(3)) { // TODO: Replace this with hasPermission once permissions are fetched from API
        route = { name: 'accounts' };
      }

      await router.replace(route);
    },
    async refreshUser() {
      const user = await UserService.getCurrentUser();
      const tenantStore = useTenantStore();

      await this.setUser(user);
      if (tenantStore?.tenantId) {
        await this.fetchUserApplications(user.id, tenantStore.tenantId)
      }
    },
    async setUser(user: User) {
      this.user = user;
      const tenantStore = useTenantStore();
      if (!tenantStore.tenantId) {
        const tenantId = user.tenant_id || null;
        tenantStore.setTenantId(tenantId);
      }

      const userPermissions = ['tenant:read', 'application:read'];
      const managerPermissions = ['application:manage', 'user:read', ...userPermissions];
      const adminPermissions = [...managerPermissions, 'administration:read', 'administration:manage']
      let permissions = userPermissions;

      if (user.roles?.map((r) => r.id).includes(1)) {
        permissions = managerPermissions;
      }
      if (user.roles?.map((r) => r.id).includes(3)) {
        permissions = adminPermissions;
      }

      this.permissions = permissions;
    },
    showToastError(message: string): string {
      const key: string = uuidv4();
      // Set toast errors
      const messages: {[id: string]: string} = {};
      Object.assign(messages, this.toastErrors);
      messages[key] = message;
      this.toastErrors = messages;

      return key;
    },
    async fetchUserApplications(userId?: number, tenantId?: number) {
      if (!userId || !tenantId) { return; }
      let applications;
      if (this.isSuperAdmin) {
        applications = await ApplicationService.getAllApplications();
      } else {
        applications = await UserService.getUserAppAccess(userId, tenantId)
      }
      this.apps = Array.from(
        applications.reduce((map: Map<number, Application>, item: { application: Application }) => {
          const app = item.application || item;
          if (!map.has(app.id)) {
            map.set(app.id, app);
          }
          return map;
        }, new Map()).values()
      );

      // this.applications = Array.from(
      //   [...appsFromGroups, ...this.user.applications].reduce((map, app) => {
      //     if (!map.has(app.id)) {
      //       map.set(app.id, app);
      //     }
      //     return map;
      //   }, new Map())
      //     .values()
      // );
    },
  }
})

function setLocalStorage(key: string, value: string) {
  if (value === null || value === undefined) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, value);
  }
}
